import React, { useEffect, useMemo, useState } from 'react';

export default function KpiCascadeModal({
                                            show,
                                            onClose,
                                            kpa,                   // target KPA object {id, name, parent_id, ...}
                                            remaining,             // remaining capacity in this KPA (100 - sum weights)
                                            loadManagerKpis,       // () => Promise<{ manager_kpa_id, items: [...] }>
                                            onSubmitCascade,       // (payload) => Promise
                                        }) {
    const [loading, setLoading] = useState(false);
    const [managerItems, setManagerItems] = useState([]);
    const [managerKpaId, setManagerKpaId] = useState(null);
    const [selected, setSelected] = useState({});
    const [overrides, setOverrides] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!show) return;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await loadManagerKpis();
                setManagerKpaId(data?.manager_kpa_id ?? null);
                const items = data?.items ?? [];
                setManagerItems(items);
                // preselect non-cascaded ones
                const sel = {}; items.forEach(i => { if (!i.already_cascaded) sel[i.id] = true; });
                setSelected(sel);
                const ov = {};
                items.forEach(i => ov[i.id] = {
                    id: i.id,
                    name: i.name,
                    weight: i.weight,
                    threshold: i.threshold,
                    target: i.target,
                    stretch: i.stretch,
                    is_percentage: i.is_percentage,
                    unit: i.is_percentage ? '%' : (i.unit || ''),
                    description: i.description || '',
                });
                setOverrides(ov);
            } catch (e) {
                setError(e?.response?.data?.message || 'Failed to load manager KPIs.');
            } finally {
                setLoading(false);
            }
        })();
    }, [show]);

    const chosen = useMemo(() => managerItems.filter(i => !!selected[i.id]), [managerItems, selected]);
    const requestedTotal = useMemo(() =>
            chosen.reduce((sum, i) => sum + Number(overrides[i.id]?.weight ?? i.weight ?? 0), 0),
        [chosen, overrides]
    );

    const toggle = (id, on) => setSelected(s => ({ ...s, [id]: on }));
    const toggleAll = (on) => {
        const next = {};
        managerItems.forEach(i => { if (!i.already_cascaded) next[i.id] = on; });
        setSelected(next);
    };
    const setOv = (id, patch) => setOverrides(o => ({ ...o, [id]: { ...(o[id] || { id }), ...patch } }));

    const canSubmit = chosen.length > 0 && remaining > 0;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        try {
            const ids = chosen.map(i => i.id);
            const ovrList = ids.map(id => {
                const v = overrides[id];
                // Ensure unit logic matches backend expectations
                const isPct = !!v.is_percentage;
                return {
                    id,
                    name: v.name,
                    weight: Number(v.weight),
                    threshold: v.threshold === '' ? null : Number(v.threshold),
                    target: v.target === '' ? null : Number(v.target),
                    stretch: v.stretch === '' ? null : Number(v.stretch),
                    is_percentage: isPct,
                    unit: isPct ? '%' : (v.unit || null),
                    description: v.description || null,
                };
            });
            await onSubmitCascade({
                mode: 'selected',
                manager_kpa_id: managerKpaId || undefined,
                kpi_ids: ids,
                overrides: ovrList,
            });
            onClose();
        } catch (e) {
            setError(e?.response?.data?.message || 'Cascade failed.');
        } finally {
            setBusy(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.15)' }}>
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Cascade KPIs from Manager</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}
                        {loading ? (
                            <div className="text-muted">Loading…</div>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => toggleAll(true)}>Select All</button>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleAll(false)}>Clear</button>
                                    </div>
                                    <div>
                                        <span className="badge bg-secondary me-2">Remaining: {Number(remaining).toFixed(2)} / 100</span>
                                        <span className={`badge ${requestedTotal > remaining ? 'bg-danger' : 'bg-info'}`}>
                      Requested: {requestedTotal.toFixed(2)}
                    </span>
                                    </div>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-sm table-hover mb-0 align-middle">
                                        <thead className="table-light">
                                        <tr>
                                            <th style={{width: 40}}></th>
                                            <th>Name</th>
                                            <th className="text-end" style={{width: 120}}>Mgr Wt</th>
                                            <th className="text-end" style={{width: 140}}>Cascade Wt</th>
                                            <th style={{width: 140}}>Thres</th>
                                            <th style={{width: 140}}>Target</th>
                                            <th style={{width: 140}}>Stretch</th>
                                            <th style={{width: 100}}>%</th>
                                            <th style={{width: 120}}>Unit</th>
                                            <th style={{width: 260}}>Description</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {managerItems.length === 0 && (
                                            <tr><td colSpan="10" className="text-center text-muted py-4">No manager KPIs.</td></tr>
                                        )}
                                        {managerItems.map(i => {
                                            const sel = !!selected[i.id];
                                            const ov = overrides[i.id] || {};
                                            return (
                                                <tr key={i.id} className={i.already_cascaded ? 'table-secondary' : ''}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            disabled={i.already_cascaded}
                                                            checked={sel}
                                                            onChange={e => toggle(i.id, e.target.checked)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input className="form-control form-control-sm"
                                                               value={ov.name ?? i.name}
                                                               onChange={e => setOv(i.id, { name: e.target.value })}/>
                                                        {i.already_cascaded && <span className="badge bg-info ms-1">Already cascaded</span>}
                                                    </td>
                                                    <td className="text-end">{Number(i.weight).toFixed(2)}</td>
                                                    <td className="text-end" style={{maxWidth: 150}}>
                                                        <input type="number" step="0.01" min="0"
                                                               className="form-control form-control-sm"
                                                               value={ov.weight ?? i.weight}
                                                               onChange={e => setOv(i.id, { weight: Number(e.target.value) })}/>
                                                    </td>
                                                    <td><input type="number" step="0.01" className="form-control form-control-sm"
                                                               value={ov.threshold ?? (i.threshold ?? '')}
                                                               onChange={e => setOv(i.id, { threshold: e.target.value })}/></td>
                                                    <td><input type="number" step="0.01" className="form-control form-control-sm"
                                                               value={ov.target ?? (i.target ?? '')}
                                                               onChange={e => setOv(i.id, { target: e.target.value })}/></td>
                                                    <td><input type="number" step="0.01" className="form-control form-control-sm"
                                                               value={ov.stretch ?? (i.stretch ?? '')}
                                                               onChange={e => setOv(i.id, { stretch: e.target.value })}/></td>
                                                    <td>
                                                        <input type="checkbox" className="form-check-input"
                                                               checked={!!ov.is_percentage}
                                                               onChange={e => setOv(i.id, { is_percentage: e.target.checked, unit: e.target.checked ? '%' : (ov.unit || '') })}/>
                                                    </td>
                                                    <td>
                                                        <input className="form-control form-control-sm"
                                                               value={ov.is_percentage ? '%' : (ov.unit || '')}
                                                               onChange={e => setOv(i.id, { unit: e.target.value })}
                                                               disabled={!!ov.is_percentage}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input className="form-control form-control-sm"
                                                               value={ov.description ?? (i.description ?? '')}
                                                               onChange={e => setOv(i.id, { description: e.target.value })}/>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
                        <button className="btn btn-primary" onClick={submit} disabled={!canSubmit || busy}>
                            {busy ? 'Cascading…' : 'Cascade Selected'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
